import { useCallback, useContext, useMemo } from 'react'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ButtonsCancelSave } from '~/components/Buttons'
import { FeedbackContext } from '~/components/feedback'
import Loading from '~/components/Loading'
import Form from '~/components/unform/Form'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'
import ProductsPanel from '~/modules/retailMedia/components/ProductsPanel'
import { useAppSelector } from '~/store/hooks'

import {
  addRelatedProductService,
  listProductResult,
  listProductResultKey
} from '../../services/campaign/product'

export const AddRelatedProduct: React.FC<AddRelatedProductProps> = ({
  showEdit,
  formRef,
  queryParams,
  handleClose,
  publisherId,
  onSuccess,
  campaignType,
  destinationPublisherIds
}) => {
  const { t, tToasts } = useAPPTranslation()
  const { toast } = useContext(FeedbackContext)

  const queryClient = useQueryClient()

  const {
    campaign: {
      products: { selectedProducts: newProducts }
    }
  } = useAppSelector(state => state.retailMedia)

  const { data } = useQuery({
    queryKey: [listProductResultKey, queryParams.campaign_id, queryParams],
    queryFn: async () => {
      const { campaign_id } = queryParams

      const response = await listProductResult(campaign_id, {
        count: false
      })

      const currentProducts: CurrentRelatedProduct[] = response.data.map(
        item => ({
          ...item,
          item_disabled: true
        })
      )

      return currentProducts
    },
    enabled: !!queryParams.campaign_id
  })

  const { mutate, isPending: loadingSave } = useMutation({
    mutationFn: addRelatedProductService,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [listProductResultKey]
      })

      tToasts({
        prefix: 'rm:ad.relatedProduct.success',
        toastFunction: toast.success
      })

      onSuccess?.()
      handleClose()
    },
    onError: () => {
      tToasts({
        prefix: 'rm:ad.relatedProduct.error',
        toastFunction: toast.critical
      })
    }
  })

  const mapProductsID = useMemo(
    () =>
      newProducts.map(({ id, product_sku, sub_publisher_id }) => ({
        id,
        product_sku,
        destinationPublisherId: sub_publisher_id
      })),
    [newProducts]
  )

  const handleSubmitEdit = useCallback(() => {
    const productIds = mapProductsID.map(p => p.id).filter(Boolean)
    const productSkus = mapProductsID.map(p => p.product_sku).filter(Boolean)

    // Offsite SKUs resolve against the destination retailer, not the campaign's own publisher.
    let destinationPublisherId: PublisherId | undefined

    if (
      campaignType === 'off_site' &&
      (destinationPublisherIds?.length ?? 0) > 1
    ) {
      const taggedDestinationPublisherIds = new Set(
        mapProductsID.map(p => p.destinationPublisherId).filter(Boolean)
      )

      if (taggedDestinationPublisherIds.size === 0) {
        tToasts({
          prefix: 'rm:ad.relatedProduct.missingDestinationError',
          toastFunction: toast.critical
        })
        return
      }

      if (taggedDestinationPublisherIds.size > 1) {
        tToasts({
          prefix: 'rm:ad.relatedProduct.mixedDestinationsError',
          toastFunction: toast.critical
        })
        return
      }

      destinationPublisherId = [
        ...taggedDestinationPublisherIds
      ][0] as PublisherId
    }

    const payload = {
      id: queryParams.campaign_id,
      body: {
        ...(productIds.length > 0
          ? { product_ids: productIds }
          : { product_skus: productSkus }),
        ...(campaignType === 'off_site' && destinationPublisherId
          ? { destination_publisher_id: destinationPublisherId }
          : {})
      }
    }

    mutate(payload)
  }, [
    mapProductsID,
    queryParams.campaign_id,
    mutate,
    campaignType,
    destinationPublisherIds,
    tToasts,
    toast
  ])

  const Buttons = () => {
    return (
      <>
        <Loading status={loadingSave}>{t('rm:savingChanges')}</Loading>

        {showEdit && !loadingSave && (
          <ButtonsCancelSave
            onCancelClick={handleClose}
            // onSaveClick={handleClickOnSave}
            isLoading={loadingSave}
            isDisabled={!mapProductsID.length}
            styleOnTop="line"
          />
        )}
      </>
    )
  }

  return (
    <div className="AddAdForm">
      <Form ref={formRef} onSubmit={handleSubmitEdit}>
        {data && (
          <ProductsPanel
            formRef={formRef}
            publisherId={publisherId}
            description={t('rm:campaign.create.ProductsPanel.description')}
            isCollapsible={false}
            isEdit={false}
            Buttons={<Buttons />}
            currentProducts={data}
            hideBrandsPanel
            campaignTypeOverride={campaignType}
            destinationPublisherIdsOverride={destinationPublisherIds}
          />
        )}
      </Form>
    </div>
  )
}
